/**
 * One hit test for every shape.
 *
 * The pointer is inverse-transformed into element-local space, then compared
 * against the flattened polylines that the renderer actually draws. Adding a
 * shape type therefore adds no hit-testing code, and hit testing can never
 * disagree with what is on screen.
 */

import type { SlateElement } from '../scene/types';
import { isArrow, isLinear } from '../scene/types';
import type { MeasureText } from './drawable';
import { polylinesOf } from './cache';
import {
  distanceToSegmentSq,
  toLocal,
  toWorld,
  worldBounds,
  rectsIntersect,
  type Rect,
  type Vec,
} from './transform';

/** Stroke tolerance in screen pixels; divided by zoom to stay constant on screen. */
export const HIT_TOLERANCE_PX = 10;
/** Handle grab radius in screen pixels. */
export const HANDLE_RADIUS_PX = 7;
/** Distance from the top edge to the rotate handle, in screen pixels. */
export const ROTATE_HANDLE_OFFSET_PX = 26;

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

export const RESIZE_HANDLES: readonly ResizeHandle[] = [
  'nw',
  'n',
  'ne',
  'e',
  'se',
  's',
  'sw',
  'w',
];

function pointInPolyline(points: readonly Vec[], p: Vec): boolean {
  // Even-odd crossing count.
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i]!;
    const b = points[j]!;
    if (a.y > p.y !== b.y > p.y) {
      const t = (p.y - a.y) / (b.y - a.y);
      if (p.x < a.x + t * (b.x - a.x)) inside = !inside;
    }
  }
  return inside;
}

export function hitTestElement(
  el: SlateElement,
  world: Vec,
  measure: MeasureText,
  zoom: number,
): boolean {
  if (el.isDeleted) return false;
  const local = toLocal(world, el);
  const tolerance = HIT_TOLERANCE_PX / Math.max(zoom, 0.01);
  for (const poly of polylinesOf(el, measure)) {
    if (poly.filled && pointInPolyline(poly.points, local)) return true;
    const limit = tolerance + poly.strokeWidth / 2;
    const limitSq = limit * limit;
    for (let i = 1; i < poly.points.length; i++) {
      if (distanceToSegmentSq(local, poly.points[i - 1]!, poly.points[i]!) <= limitSq) return true;
    }
  }
  return false;
}

/** Topmost element under the pointer, or null. Iterates z-order backwards. */
export function pickElement(
  elements: readonly SlateElement[],
  world: Vec,
  measure: MeasureText,
  zoom: number,
): SlateElement | null {
  for (let i = elements.length - 1; i >= 0; i--) {
    const el = elements[i]!;
    if (hitTestElement(el, world, measure, zoom)) return el;
  }
  return null;
}

/** Marquee selection: any element whose world bounds overlap the rectangle. */
export function elementsInRect(elements: readonly SlateElement[], rect: Rect): SlateElement[] {
  return elements.filter((el) => !el.isDeleted && rectsIntersect(worldBounds(el), rect));
}

export function handleLocalPoint(handle: ResizeHandle, width: number, height: number): Vec {
  const x = handle.includes('w') ? 0 : handle.includes('e') ? width : width / 2;
  const y = handle.includes('n') ? 0 : handle.includes('s') ? height : height / 2;
  return { x, y };
}

/** The corner held fixed in world space while dragging `handle`. */
export function anchorLocalPoint(handle: ResizeHandle, width: number, height: number): Vec {
  const x = handle.includes('w') ? width : handle.includes('e') ? 0 : 0;
  const y = handle.includes('n') ? height : handle.includes('s') ? 0 : 0;
  return { x, y };
}

export function handleWorldPoint(handle: ResizeHandle, el: SlateElement): Vec {
  return toWorld(handleLocalPoint(handle, el.width, el.height), el);
}

export function rotateHandleWorld(el: SlateElement, zoom: number): Vec {
  const offset = ROTATE_HANDLE_OFFSET_PX / Math.max(zoom, 0.01);
  return toWorld({ x: el.width / 2, y: -offset }, el);
}

/** Boxes, text and freedraw get box handles; arrows get endpoint handles instead. */
export function supportsBoxHandles(el: SlateElement): boolean {
  return !isArrow(el);
}

export type HandleHit =
  | { readonly kind: 'resize'; readonly handle: ResizeHandle }
  | { readonly kind: 'rotate' }
  | { readonly kind: 'point'; readonly index: number };

export function pickHandle(el: SlateElement, world: Vec, zoom: number): HandleHit | null {
  const radius = HANDLE_RADIUS_PX / Math.max(zoom, 0.01);
  const radiusSq = radius * radius;
  const near = (p: Vec) => {
    const dx = world.x - p.x;
    const dy = world.y - p.y;
    return dx * dx + dy * dy <= radiusSq;
  };

  if (isArrow(el)) {
    for (let i = 0; i < el.points.length; i++) {
      const p = el.points[i]!;
      if (near(toWorld({ x: p[0], y: p[1] }, el))) return { kind: 'point', index: i };
    }
    return null;
  }

  if (near(rotateHandleWorld(el, zoom))) return { kind: 'rotate' };
  for (const handle of RESIZE_HANDLES) {
    if (near(handleWorldPoint(handle, el))) return { kind: 'resize', handle };
  }
  return null;
}

const CURSOR_BY_OCTANT = [
  'ew-resize',
  'nwse-resize',
  'ns-resize',
  'nesw-resize',
  'ew-resize',
  'nwse-resize',
  'ns-resize',
  'nesw-resize',
] as const;

/**
 * Cursor derived from the handle's WORLD direction, so a rotated element's
 * handles do not point the wrong way.
 */
export function cursorForHandle(handle: ResizeHandle, angle: number): string {
  const dx = handle.includes('e') ? 1 : handle.includes('w') ? -1 : 0;
  const dy = handle.includes('s') ? 1 : handle.includes('n') ? -1 : 0;
  const base = Math.atan2(dy, dx) + angle;
  let deg = (base * 180) / Math.PI;
  deg = ((deg % 360) + 360) % 360;
  return CURSOR_BY_OCTANT[Math.round(deg / 45) % 8]!;
}

/** Selection outline for one element, in world space, as four corners. */
export function selectionCorners(el: SlateElement): Vec[] {
  return [
    toWorld({ x: 0, y: 0 }, el),
    toWorld({ x: el.width, y: 0 }, el),
    toWorld({ x: el.width, y: el.height }, el),
    toWorld({ x: 0, y: el.height }, el),
  ];
}

export function selectionBounds(elements: readonly SlateElement[]): Rect | null {
  if (elements.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const el of elements) {
    for (const c of selectionCorners(el)) {
      minX = Math.min(minX, c.x);
      minY = Math.min(minY, c.y);
      maxX = Math.max(maxX, c.x);
      maxY = Math.max(maxY, c.y);
    }
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

export function isLinearElement(el: SlateElement): boolean {
  return isLinear(el);
}
